import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSettings } from "src/services/configService";

import { ContentContainer, SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { Typography } from "@mui/material";
import { DangerButton, PrimaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useUpdateWorkspaceSettingMutation } from "../../api";
import { useCurrentWorkspace } from "../../api/helpers";

import { useStyles } from "./WorkspaceConfigurationPanel.style";
import {
    buildWorkspaceSettingsState,
    workspaceFeatureSettingNames,
    workspacePageSettingNames,
} from "./WorkspaceConfigurationPanel.utils";
import { WorkspaceSettingRow } from "./WorkspaceSettingRow";

export const WorkspaceConfigurationPanel = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const { data: workspace, isLoading: isWorkspaceLoading } = useCurrentWorkspace();
    const workspaceId = workspace?.id;

    const valueByName = useMemo(
        () => (workspace ? buildWorkspaceSettingsState(workspace.settings ?? []).valueByName : null),
        [workspace],
    );

    const pendingRef = useRef(new Set<API.WorkspaceSettingName>());
    const [pendingNames, setPendingNames] = useState(() => new Set<API.WorkspaceSettingName>());
    const [twoFactorDialog, setTwoFactorDialog] = useState<{ nextChecked: boolean } | null>(null);

    const [updateSetting] = useUpdateWorkspaceSettingMutation();

    const submitWorkspaceSetting = async (name: API.WorkspaceSettingName, checked: boolean) => {
        if (!workspaceId || pendingRef.current.has(name)) {
            return;
        }
        pendingRef.current.add(name);
        setPendingNames(new Set(pendingRef.current));
        try {
            await updateSetting({ id: workspaceId, name, value: checked ? 1 : 0 }).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.workspaceSettingUpdated"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:workspacePage.toast.workspaceSettingUpdateFailed"));
        } finally {
            pendingRef.current.delete(name);
            setPendingNames(new Set(pendingRef.current));
        }
    };

    const handleToggle = (name: API.WorkspaceSettingName, checked: boolean) => {
        if (name === "MembersRequireTwoFactorAuthentication") {
            setTwoFactorDialog({ nextChecked: checked });
            return;
        }
        void submitWorkspaceSetting(name, checked);
    };

    const confirmTwoFactorChange = () => {
        if (!twoFactorDialog) {
            return;
        }
        const { nextChecked } = twoFactorDialog;
        setTwoFactorDialog(null);
        void submitWorkspaceSetting("MembersRequireTwoFactorAuthentication", nextChecked);
    };

    const rowIsBusy = (name: API.WorkspaceSettingName) =>
        pendingNames.has(name) || (name === "MembersRequireTwoFactorAuthentication" && twoFactorDialog !== null);

    const showLoading = isWorkspaceLoading && workspace === null;

    const { features } = getSettings();
    const visibleFeatureSettingNames = workspaceFeatureSettingNames.filter(
        (name) => name !== "OverviewPageShowsActivityChart" || features.eventChartEnabled,
    );

    return (
        <SectionContainer className={classes.root}>
            <SectionHeader>{t("pages:workspacePage.section.configuration.title")}</SectionHeader>
            <Typography variant="body1" component="p" className={classes.sectionSubtitle}>
                {t("pages:workspacePage.section.configuration.subtitle")}
            </Typography>

            {twoFactorDialog !== null ? (
                <Modal
                    open
                    title={
                        twoFactorDialog.nextChecked
                            ? t("pages:workspacePage.configuration.twoFactorConfirm.titleEnable")
                            : t("pages:workspacePage.configuration.twoFactorConfirm.titleDisable")
                    }
                    onClose={() => setTwoFactorDialog(null)}
                    secondaryActionText={t("common:button.cancel")}
                    endButton={
                        twoFactorDialog.nextChecked ? (
                            <PrimaryButton onClick={confirmTwoFactorChange}>
                                {t("pages:workspacePage.configuration.twoFactorConfirm.confirmEnable")}
                            </PrimaryButton>
                        ) : (
                            <DangerButton onClick={confirmTwoFactorChange}>
                                {t("pages:workspacePage.configuration.twoFactorConfirm.confirmDisable")}
                            </DangerButton>
                        )
                    }
                >
                    <Typography color="text.primary" variant="body1" component="p">
                        {twoFactorDialog.nextChecked
                            ? t("pages:workspacePage.configuration.twoFactorConfirm.descriptionEnable")
                            : t("pages:workspacePage.configuration.twoFactorConfirm.descriptionDisable")}
                    </Typography>
                </Modal>
            ) : null}

            <ContentContainer className={classes.card}>
                {showLoading || valueByName === null ? (
                    <LoadingBox sx={{ marginY: 4 }} />
                ) : (
                    <>
                        <Typography variant="subtitle1" component="h3" className={classes.settingsGroupTitle}>
                            {t("pages:workspacePage.configuration.groups.pages")}
                        </Typography>
                        {workspacePageSettingNames.map((name) => (
                            <WorkspaceSettingRow
                                key={name}
                                name={name}
                                value={valueByName[name]}
                                isBusy={rowIsBusy(name)}
                                onToggle={handleToggle}
                            />
                        ))}
                        <Typography variant="subtitle1" component="h3" className={classes.settingsGroupTitle}>
                            {t("pages:workspacePage.configuration.groups.features")}
                        </Typography>
                        {visibleFeatureSettingNames
                            // temporary: RolloutPageEnabled is hidden from workspace settings until the Rollouts page is ready for all users
                            // issue: https://gitlab.com/emteria/development/web/frontend/hub/-/work_items/1103
                            .filter((name) => name !== "RolloutPageEnabled")
                            .map((name) => (
                                <WorkspaceSettingRow
                                    key={name}
                                    name={name}
                                    value={valueByName[name]}
                                    isBusy={rowIsBusy(name)}
                                    reserveLeadingIconSpace={name === "MembersRequireTwoFactorAuthentication"}
                                    onToggle={handleToggle}
                                />
                            ))}
                    </>
                )}
            </ContentContainer>
        </SectionContainer>
    );
};
